import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import queryString from "query-string";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import ReportService from "../../../services/reportService";

import { generateCSV, downloadEmployeeSummaryByDepartmentCSV } from "../utils";

import { formatDate } from "../../../helpers/date";

import * as exportActions from "../../../actions/exportActions";

import Filters from "./EmployeeSummaryByDepartmentFilters";

import EmployeeSummaryByDepartmentList from "./EmployeeSummaryByDepartmentList";

import "./EmployeeSummaryByDepartmentPage.scss";

const actualTypes = ["CAPITAL", "AS BILLED", "CREDIT", "OVERHEAD", "PERSONAL"];

const billedTypes = [
  "PREPAID",
  "BILLABLE",
  "FIXED FEE",
  "RETAINER",
  "CLIENTCARE",
  "TIER1",
  "TIER2",
  "CAPITAL",
  "MANAGED SERVICES"
];

const columns = [
  "Prepaid Billed",
  "T&M Billed",
  "Fix Fee Billed",
  "Retainer Billed",
  "Client Care Billed",
  "Tiered 1 Billed",
  "Tiered 2 Billed",
  "Capital Billed",
  "Managed Serv Billable",
  "Total Billed",
  "Billable Actual",
  "Capital Actual",
  "As Bill Actual",
  "Credit Actual",
  "Overhead Actual",
  "Personal Actual",
  "Total Actual",
  "Billed Util Rate",
  "Total Util Rate"
];

const EmployeeSummaryByDepartmentPage = ({ exported, exporting, actions }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: formatDate(moment().startOf("month").toDate(), "yyyy-MM-dd"),
    endDate: formatDate(new Date(), "yyyy-MM-dd")
  });
  const [data, setData] = useState([]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Employee-Summary-By-Department-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setData([]);
    const query = queryString.stringify(
      {
        ...filters,
        startDate: moment(filters.startDate)
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        endDate: moment(filters.endDate)
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss")
      },
      { arrayFormat: "bracket" }
    );
    const result = await ReportService.get(
      `/employee-summary-by-department?${query}`
    );
    setData(result);
    setLoading(false);
  }, [filters]);

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      (name === "departments" ||
        name === "customers" ||
        name === "managers" ||
        name === "projects")
    ) {
      setFilters((prev) => ({ ...prev, [name]: value.map((v) => v.value) }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleResetClick = () => {
    setFilters({
      startDate: formatDate(moment().startOf("month").toDate(), "yyyy-MM-dd"),
      endDate: formatDate(new Date(), "yyyy-MM-dd")
    });
    setData([]);
  };

  const getDaysDifference = (month, year) => {
    const startDate = moment([year, month - 1]).startOf("month");
    const endDate = moment([year, month - 1])
      .endOf("month")
      .endOf("day");
    const daysDifference = Math.round(
      (endDate - startDate + 1) / (1000 * 60 * 60 * 24)
    );
    let daysRemaining = daysDifference;
    let clonedDate = moment(startDate).clone();
    while (clonedDate <= moment(endDate)) {
      if (clonedDate.day() === 0 || clonedDate.day() === 6) {
        daysRemaining -= 1;
      }
      clonedDate = clonedDate.add(1, "days");
    }
    return daysRemaining;
  };

  const generateTotalsToExport = (users, labels, month, year) => {
    let totalBilledSum = 0;
    let totalBillableActual = 0;
    let totalAsBilledActual = 0;
    let totalCreditActual = 0;
    let totalOverheadActual = 0;
    let totalCapitalSum = 0;
    let billedUtilRateSum = 0;
    let totalUtilRateSum = 0;
    let totalPersonalActual = 0;
    const totalActual = 0;
    const billedColums = labels.map((label, i) => {
      let callTypeTotal = 0;
      users.forEach((u) => {
        let tempBilledUtilRateSum = 0;
        let tempTotalUtilRateSum = 0;
        u.callTypes.forEach((callType) => {
          if (callType.callTypeName === label) {
            callTypeTotal += callType.billableHours;
            tempBilledUtilRateSum += callType.actualHours;
            tempTotalUtilRateSum += callType.actualHours;
            totalBilledSum += callType.billableHours || 0;
            totalBillableActual += callType.actualHours;
          } else if (callType.callTypeName === "AS BILLED" && i === 0) {
            totalAsBilledActual += callType.actualHours;
            tempTotalUtilRateSum += callType.actualHours;
          } else if (callType.callTypeName === "CREDIT" && i === 0) {
            totalCreditActual += callType.actualHours;
          } else if (callType.callTypeName === "OVERHEAD" && i === 0) {
            totalOverheadActual += callType.actualHours;
          } else if (callType.callTypeName === "CAPITAL" && i === 0) {
            totalCapitalSum += callType.actualHours;
            tempTotalUtilRateSum += callType.actualHours;
          } else if (callType.callTypeName === "PERSONAL" && i === 0) {
            totalPersonalActual += callType.actualHours;
          }
        });
        const difference = getDaysDifference(month, year) * 8;
        billedUtilRateSum += (tempBilledUtilRateSum / difference) * 100;
        totalUtilRateSum += (tempTotalUtilRateSum / difference) * 100;
      });
      return !Number.isNaN(callTypeTotal)
        ? callTypeTotal.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,")
        : "";
    });
    const information = [
      ...billedColums,
      totalBilledSum.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalBillableActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalCapitalSum.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalAsBilledActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalCreditActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalOverheadActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalPersonalActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      totalActual.toFixed(1).replace(/\d(?=(\d{3})+\.)/g, "$&,"),
      `${(billedUtilRateSum / users.length)
        .toFixed(1)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")} %`,
      `${(totalUtilRateSum / users.length)
        .toFixed(1)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,")} %`
    ];
    const objectToReturn = {};
    information.forEach((info, index) => {
      objectToReturn[columns[index]] = info;
    });
    return objectToReturn;
  };

  const getEmploymentStatusLabel = (employmentStatus) => {
    let label = "";
    switch (employmentStatus) {
      case "Consultant":
        label = " (C)";
        break;
      case "Intern":
        label = " (I)";
        break;
      case "Hourly":
        label = " (H)";
        break;
      default:
        break;
    }
    return label;
  };

  const generateBillableActual = (user) => {
    let totalActual = 0;
    user.callTypes.forEach((callType) => {
      if (callType.isBillable) {
        totalActual += callType.actualHours;
      }
    });
    return totalActual;
  };

  const generateUtilRate = (user, totalType = false, month, year) => {
    let tempBilledUtilRate = 0;
    let tempTotalUtilRate = 0;
    user.callTypes.forEach((callType) => {
      if (callType.isBillable) {
        tempBilledUtilRate += callType.actualHours;
        tempTotalUtilRate += callType.actualHours;
      }
      if (callType.callTypeName === "AS BILLED") {
        tempTotalUtilRate += callType.actualHours;
      }
      if (callType.callTypeName === "CAPITAL") {
        tempTotalUtilRate += callType.actualHours;
      }
    });
    const difference = getDaysDifference(month, year) * 8;
    if (user.employmentStatus === "Salary") {
      return totalType
        ? ((tempTotalUtilRate / difference) * 100).toFixed(2)
        : ((tempBilledUtilRate / difference) * 100).toFixed(2);
    }
    return totalType
      ? ((tempTotalUtilRate / user.actualHours || 0) * 100).toFixed(2)
      : ((tempBilledUtilRate / user.actualHours || 0) * 100).toFixed(2);
  };

  const generateDataToExport = () => {
    const result = data.map((department) => {
      const objectToReturn = {
        header: [],
        body: [],
        footer: []
      };
      objectToReturn.header.push(
        `${department.code} - ${department.month}/${department.year}`
      );
      columns.forEach((column) => objectToReturn.header.push(column));
      department.employees.forEach((employee) => {
        let userInformation = {
          [`${department.code} - ${department.month}/${department.year}`]: `${
            employee.employeeName
          } ${getEmploymentStatusLabel(employee.employmentStatus)}`
        };
        billedTypes.forEach((type, index) => {
          userInformation = {
            ...userInformation,
            [columns[index]]:
              employee.callTypes
                .find((t) => t.callTypeName === type)
                ?.billableHours?.toFixed(2) || 0
          };
        });
        userInformation = {
          ...userInformation,
          "Total Billed": employee.billableHours?.toFixed(2) || 0
        };
        userInformation = {
          ...userInformation,
          "Billable Actual": generateBillableActual(employee)
        };
        actualTypes.forEach((type, index) => {
          userInformation = {
            ...userInformation,
            [columns[index + 11]]:
              employee.callTypes
                .find((t) => t.callTypeName === type)
                ?.actualHours?.toFixed(2) || 0
          };
        });
        userInformation = {
          ...userInformation,
          "Total Actual": employee.actualHours?.toFixed(2) || 0
        };
        userInformation = {
          ...userInformation,
          "Billed Util Rate": generateUtilRate(
            employee,
            false,
            department.month,
            department.year
          )
        };
        userInformation = {
          ...userInformation,
          "Total Util Rate": generateUtilRate(
            employee,
            true,
            department.month,
            department.year
          )
        };
        objectToReturn.body.push(userInformation);
      });
      const footerTotals = generateTotalsToExport(
        department.employees,
        billedTypes,
        department.month,
        department.year
      );

      objectToReturn.footer = {
        [`${department.code} - ${department.month}/${department.year}`]: `${department.code} - ${department.description}`,
        ...footerTotals
      };
      return objectToReturn;
    });
    return result;
  };

  const handleExport = async () => {
    const information = generateDataToExport();
    const csvData = information.map((info) =>
      generateCSV(info.header, [...info.body, ...[info.footer]], `\n\n`)
    );

    const title = `Employee Summary By Department from ${moment(
      filters.startDate
    ).format("MM/DD/YYYY")} to ${moment(filters.endDate).format("MM/DD/YYYY")}`;

    downloadEmployeeSummaryByDepartmentCSV(
      `EmployeeSummary-${formatDate(moment(), "yyyyMMdd")}`,
      csvData,
      title
    );
  };

  const handlePDFExport = () => {
    const queryFilters = {
      ...filters,
      startDate: moment(filters.startDate)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment(filters.endDate)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss")
    };
    const payload = {
      data: queryFilters,
      type: "EMPLOYEE_SUMMARY_BY_DEPARTMENT",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div id="employee-summary-by-department-page" className="report-page">
      <Filters
        filters={filters}
        onChange={handleChange}
        onFetchClick={fetchData}
        onResetClick={handleResetClick}
        loading={loading}
        onCSVExport={handleExport}
        onPDFExport={handlePDFExport}
        exportDisabled={!data?.length || exporting}
        disabled={
          loading ||
          !Object.keys(filters).length ||
          !filters.startDate ||
          !filters.endDate ||
          !moment(filters.startDate).isBefore(filters.endDate)
        }
      />
      <EmployeeSummaryByDepartmentList
        data={data}
        loading={loading}
        filters={filters}
      />
    </div>
  );
};

EmployeeSummaryByDepartmentPage.propTypes = {
  actions: PropTypes.object,
  exported: PropTypes.bool,
  exporting: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    exported: state.reducers.exported,
    exporting: state.reducers.exporting
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...exportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(EmployeeSummaryByDepartmentPage);
